import { onMounted, onUnmounted } from "vue";
import { usePiniaStore } from "@/stores/store";
import { signOut } from "aws-amplify/auth";
import { redirectToRoute } from "@/router";
import { showAlert } from "@/methods/errorHandling";
import { AuthRouteNames } from "@/constants/routes";

export const endInactiveUserSession = (timeoutMinutes = 5) => {
    /**
     * Automatically log out the user after a period of inactivity
     * or when the browser tab/window is closed.
     * @param {number} timeoutMinutes
     */
    const pStore = usePiniaStore();
    let inactivityTimer: ReturnType<typeof setTimeout>;

    const logout = async () => {
        try {
            await signOut({ global: true });
        } finally {
            pStore.clearUserSession();
            showAlert("alerts.expiredSession");
            redirectToRoute({ name: AuthRouteNames.LOGIN });
        }
    };

    const resetInactivityTimer = () => {
        /**
         * Resets the inactivity timer. If no user activity occurs within `timeoutMinutes`,
         * the logout function will be called automatically.
         * Should be bound to user interaction events like mouse movement and key presses.
         */
        clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(
            () => {
                logout();
            },
            timeoutMinutes * 60 * 1000,
        );
    };

    onMounted(() => {
        // Start the inactivity timer on component mount
        resetInactivityTimer();

        // Reset timer on user interactions
        ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach(
            (evt) => window.addEventListener(evt, resetInactivityTimer),
        );
    });

    onUnmounted(() => {
        // Clear the timer and detach all event listeners when component unmounts
        clearTimeout(inactivityTimer);
        ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach(
            (evt) => window.removeEventListener(evt, resetInactivityTimer),
        );
    });
};
